"use client";
import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import { TableOverviewUser, WL_PRESENCE_OFFICE } from "@gettactic/api";
import { InfiniteScroll } from "@gettactic/components/src/components/InfiniteScroll/InfiniteScroll";
import { cn, limitWeekDateStrings } from "@gettactic/helpers";
import { useAuthenticated } from "@gettactic/hooks";
import { format, parse } from "date-fns";
import pluralize from "pluralize";
import React, { useMemo } from "react";
import { useTeamAttendanceFilters } from "@/features/teams/components/TeamSchedule/hooks/useTeamAttendanceFilters";
import { TeamAttendanceFilters } from "@/features/teams/components/TeamSchedule/TeamAttendanceFilters";

interface MobilePeopleSectionProps {
  daysCount: number;
  selectedDayIndex: number;
}

// Single-letter day label derived from the date so it stays aligned with the
// configurable week start day
const dayLabel = (dateStr: string): string =>
  format(parse(dateStr, "yyyy-MM-dd", new Date()), "EEEEE");

const isInOffice = (location: string | null | undefined): boolean => {
  return location === WL_PRESENCE_OFFICE;
};

export const MobilePeopleSection: React.FC<MobilePeopleSectionProps> = ({
  daysCount,
  selectedDayIndex
}) => {
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();

  // Fetch full week data (no selectedDayIndex = full week)
  const {
    filters: dashboardFilters,
    isComponentLoading,
    totalUsers,
    tableOverviewData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    setTeams,
    handleSaveFilterData
  } = useTeamAttendanceFilters({
    authenticatedUser,
    currentOffice
  });

  const formattedWeek = useMemo(() => {
    try {
      return (
        dashboardFilters.week?.map((date) => format(date, "yyyy-MM-dd")) || []
      );
    } catch {
      return [];
    }
  }, [dashboardFilters.week]);

  const limitedWeek = limitWeekDateStrings(formattedWeek, daysCount);

  const scrollRef = React.useRef<HTMLDivElement>(null);

  return (
    <div className="px-4 pt-2">
      {/* Header */}
      <div className="flex items-center gap-2 pb-2">
        <div className="flex-1 text-[15px] text-neutral-1000">
          <strong>{totalUsers}</strong> {pluralize("people", totalUsers)} in
          office
        </div>
        <TeamAttendanceFilters
          teams={dashboardFilters.teams}
          onTeamsChange={setTeams}
          onSaveFilterData={handleSaveFilterData}
          isComponentLoading={isComponentLoading}
          triggerClassName="rounded-md border-1 border-neutral-100 px-3.5 py-3"
        />
      </div>

      {/* People list */}
      <div
        ref={scrollRef}
        className="rounded-xl border border-neutral-100 bg-white"
      >
        {tableOverviewData.map((person) => (
          <PersonWeeklyCard
            key={person.user.id}
            person={person}
            weekDates={limitedWeek}
            selectedDayIndex={selectedDayIndex}
          />
        ))}

        {isLoading && tableOverviewData.length === 0 && (
          <div className="flex items-center justify-center py-8 text-neutral-400 text-sm">
            Loading...
          </div>
        )}

        {!isLoading && tableOverviewData.length === 0 && (
          <div className="flex items-center justify-center py-8 text-neutral-400 text-sm">
            No people to show
          </div>
        )}

        {hasNextPage && (
          <InfiniteScroll
            hasNextPage={!!hasNextPage}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={!!isFetchingNextPage}
            disabled={!!isLoading || tableOverviewData.length === 0}
            offset={200}
            scrollRef={scrollRef}
            spinner={
              <div className="flex items-center justify-center py-4 text-neutral-400 text-sm">
                Loading more...
              </div>
            }
          />
        )}
      </div>
    </div>
  );
};

const PersonWeeklyCard: React.FC<{
  person: TableOverviewUser;
  weekDates: string[];
  selectedDayIndex: number;
}> = ({ person, weekDates, selectedDayIndex }) => {
  return (
    <div className="flex items-center gap-3 border-neutral-50 border-b px-3 py-2.5 last:border-b-0">
      <UserAvatar user={person.user} size="sm" />
      <p className="min-w-0 flex-1 truncate font-medium text-neutral-900 text-sm">
        {person.user.name}
      </p>
      <div className="flex shrink-0 gap-1">
        {weekDates.map((dateStr, index) => {
          const wl = person.working_locations?.[dateStr];
          const inOffice = isInOffice(wl?.location);
          const isSelectedDay = index === selectedDayIndex;

          return (
            <span
              key={dateStr}
              className={cn(
                "flex h-5 w-5 items-center justify-center rounded font-semibold text-[10px]",
                inOffice
                  ? isSelectedDay
                    ? "bg-primary text-white"
                    : "bg-primary/10 text-primary"
                  : "bg-neutral-100 text-neutral-400"
              )}
            >
              {dayLabel(dateStr)}
            </span>
          );
        })}
      </div>
    </div>
  );
};
